"use server";

import { auth } from "@/auth";
import type { ActionResult } from "@/types/server-action";
import type {
  ParticulierSearchOutcome,
  SiretLookupResult,
} from "@/lib/cat/client-step-contracts";
import { resolveParticulierSearch } from "@/lib/cat/particulier-search-resolve";
import { resolveSiretLookup } from "@/lib/cat/siret-resolve";
import {
  catParticulierSearchBodySchema,
  type CatParticulierSearchBody,
} from "@/lib/schemas/cat/particulier-search";
import {
  saveDraftClientDataSchema,
  type SaveDraftClientDataInput,
} from "@/lib/schemas/cat/client-step-save-draft";
import { catSiretVerifyBodySchema } from "@/lib/schemas/cat/siret-verify";
import { validateParams } from "@/lib/utils";
import { updateDraftAfterStepSave } from "@/lib/cat/draft-step-save";
import {
  buildDraftLockHolder,
  DraftLockDeniedError,
} from "@/lib/cat/draft-lock";
import type { Draft } from "@/prisma/generated/client";
import { Prisma } from "@/prisma/generated/client";

export async function searchCatParticulierAction(
  input: CatParticulierSearchBody,
): Promise<ActionResult<ParticulierSearchOutcome>> {
  const session = await auth();
  if (!session) {
    return { message: "Non authentifié.", success: false };
  }

  const parsed = catParticulierSearchBodySchema.safeParse(input);
  if (!parsed.success) {
    const msg =
      parsed.error.issues[0]?.message ?? "Données de recherche invalides.";
    return { message: msg, success: false };
  }

  try {
    const { context, ...criteria } = parsed.data;
    const data = await resolveParticulierSearch(criteria, {
      internalEmployeeOnly: context === "avs",
    });
    return { data, success: true };
  } catch (error) {
    console.error("[searchCatParticulierAction]", error);
    return {
      message: "Une erreur serveur est survenue.",
      success: false,
    };
  }
}

export async function verifyCatSiretAction(
  siretRaw: string,
): Promise<ActionResult<SiretLookupResult>> {
  const session = await auth();
  if (!session) {
    return { message: "Non authentifié.", success: false };
  }

  const parsed = catSiretVerifyBodySchema.safeParse({ siret: siretRaw });
  if (!parsed.success) {
    const msg = parsed.error.issues[0]?.message ?? "SIRET invalide.";
    return { message: msg, success: false };
  }

  try {
    const data = await resolveSiretLookup(parsed.data.siret);
    return { data, success: true };
  } catch (error) {
    console.error("[verifyCatSiretAction]", error);
    return {
      message: "Une erreur serveur est survenue.",
      success: false,
    };
  }
}

export async function saveDraftData(
  params: SaveDraftClientDataInput,
): Promise<ActionResult<Draft>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }
    const clientdata = validateParams(saveDraftClientDataSchema, params);
    const { id, ...data } = clientdata;

    const sessionKvps = session.data?.kvps?.current?.identifier ?? String(id);

    const lockHolder = buildDraftLockHolder(session.user);

    const savedDraft = await updateDraftAfterStepSave({
      draftId: id,
      stepId: "client",
      userId: lockHolder.userId,
      userName: lockHolder.userName,
      kvps: sessionKvps,
      data: {
        ...data,
        saleTypeTags: Prisma.DbNull,
      },
    });
    return { success: true, data: savedDraft };
  } catch (error) {
    console.error(error);

    if (error instanceof DraftLockDeniedError) {
      return { success: false, message: error.message };
    }

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
